import React from "react";
import {
  GetStaticPathsResult,
  GetStaticProps,
  GetStaticPropsResult,
  NextPage,
} from "next";
import { uniqBy } from "lodash/fp";
import { OakBox } from "@oaknational/oak-components";

import Layout from "@/components/AppComponents/AppLayout";
import CMSClient from "@/node-lib/cms";
import { BlogPost } from "@/common-lib/cms-types";
import {
  getFallbackBlockingConfig,
  shouldSkipInitialBuild,
} from "@/node-lib/isr";
import { BlogJsonLd } from "@/browser-lib/seo/getJsonLd";
import BlogPortableText from "@/components/GenericPagesComponents/PostPortableText/PostPortableText";
import { getSeoProps } from "@/browser-lib/seo/getSeoProps";
import { imageBuilder } from "@/components/HooksAndUtils/sanityImageBuilder";
import { getBlogWebinarPostBreadcrumbs } from "@/components/SharedComponents/Breadcrumbs/getBreadcrumbs";
import PostSingleLayout from "@/components/SharedComponents/PostSingleLayout";
import getPageProps from "@/node-lib/getPageProps";
import curriculumApi2023 from "@/node-lib/curriculum-api-2023";
import { TopNavProps } from "@/components/AppComponents/TopNav/TopNav";
import { TeacherBrowseAnalyticsStoreProvider } from "@/context/TeacherBrowseAnalytics/TeacherBrowseAnalyticsProvider";

export type SerializedBlog = Omit<BlogPost, "date"> & {
  date: string;
};

export type BlogSinglePageProps = {
  blog: SerializedBlog;
  categories: { title: string; slug: string }[];
  topNav: TopNavProps;
};

const BlogSinglePage: NextPage<BlogSinglePageProps> = (props) => {
  const { blog, categories, topNav } = props;

  return (
    <TeacherBrowseAnalyticsStoreProvider
      programmeState={null}
      accessLevel="blogs_embedded_links"
    >
      <Layout
        topNavProps={topNav}
        seoProps={getSeoProps({
          ...props.blog.seo,
          title: props.blog.seo?.title || props.blog.title,
          description:
            props.blog.seo?.description || props.blog.summaryPortableText,
          imageUrl: imageBuilder
            .image(props.blog.mainImage)
            .width(1400)
            .height(700)
            .fit("crop")
            .crop("center")
            .url(),
        })}
        $background="bg-primary"
      >
        <PostSingleLayout
          content={props}
          breadcrumbs={getBlogWebinarPostBreadcrumbs(
            categories,
            blog,
            "blog",
            "Blog",
          )}
        >
          <OakBox $mt="spacing-48">
            <BlogPortableText portableText={props.blog.contentPortableText} />
          </OakBox>
        </PostSingleLayout>
        <BlogJsonLd blog={props.blog} />
      </Layout>
    </TeacherBrowseAnalyticsStoreProvider>
  );
};

type URLParams = { blogSlug: string };

export const getStaticPaths = async () => {
  if (shouldSkipInitialBuild) {
    return getFallbackBlockingConfig();
  }

  const blogResults = await CMSClient.blogPosts();

  const paths = blogResults.map((blog) => ({
    params: { blogSlug: blog.slug },
  }));

  const config: GetStaticPathsResult<URLParams> = {
    fallback: "blocking",
    paths,
  };
  return config;
};

export const getStaticProps: GetStaticProps<
  BlogSinglePageProps,
  URLParams
> = async (context) => {
  return getPageProps({
    page: "blog-single::getStaticProps",
    context,
    getProps: async () => {
      const blogSlug = context.params?.blogSlug as string;
      const isPreviewMode = context.preview === true;

      const blogResult = await CMSClient.blogPostBySlug(blogSlug, {
        previewMode: isPreviewMode,
      });

      const blogResults = await CMSClient.blogPosts();
      const categories = uniqBy(
        "title",
        blogResults.map((blogResult) => blogResult.category),
      ).sort((a, b) => (a.title < b.title ? -1 : 1));

      if (!blogResult) {
        return {
          notFound: true,
        };
      }

      const blog = {
        ...blogResult,
        date: blogResult.date.toISOString(),
      };

      const topNav = await curriculumApi2023.topNav();

      const results: GetStaticPropsResult<BlogSinglePageProps> = {
        props: {
          categories,
          blog,
          topNav,
        },
      };
      return results;
    },
  });
};

export default BlogSinglePage;
